/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Generate Playbook Tab
 * 
 * Wizard-style UI for generating a single playbook from a knowledge source.
 * Uses the source_query tool via an agent to research and synthesize content.
 */

import React, { useState, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { 
  Sparkles,
  BookOpen,
  Loader2,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';

// Components
import { DynamicSourceSelector, SelectedSource } from './DynamicSourceSelector';

// Services
import {
  listKnowledgeSources,
  generateSinglePlaybook,
} from '@/services/skillshop';
import type { 
  GenerateSinglePlaybookResponseData,
} from '@/services/skillshop';

type GenerationStep = 'configure' | 'generating' | 'success' | 'error';
type GenerationPhase = 'indexing' | 'researching' | 'writing' | 'classifying' | 'saving';
type CategoryOption = 'auto' | 'product' | 'organizational';

export function GeneratePlaybookTab() {
  const { toast } = useToast();
  
  // Form state - using the new SelectedSource type from DynamicSourceSelector
  const [selectedSource, setSelectedSource] = useState<SelectedSource | null>(null);
  const [topic, setTopic] = useState<string>('');
  const [category, setCategory] = useState<CategoryOption>('auto');
  
  // Generation state
  const [step, setStep] = useState<GenerationStep>('configure');
  const [phase, setPhase] = useState<GenerationPhase>('indexing');
  const [result, setResult] = useState<GenerateSinglePlaybookResponseData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  
  // Fetch knowledge sources
  const { data: sources, isLoading: isLoadingSources } = useQuery({
    queryKey: ['knowledge-sources'],
    queryFn: listKnowledgeSources,
    staleTime: 60 * 1000,
  });
  
  // Handle source selection from DynamicSourceSelector
  const handleSourceSelected = useCallback((source: SelectedSource | null) => {
    setSelectedSource(source);
  }, []);
  
  // Generate playbook mutation
  const generateMutation = useMutation({
    mutationFn: async () => {
      if (!selectedSource) {
        throw new Error('No source selected');
      }
      
      // Simulate phase updates (the actual phases happen server-side)
      setPhase('indexing');
      
      // Start the generation
      const categoryValue = category === 'auto' ? undefined : category;
      
      // Poll simulation - in reality this happens server-side
      const phaseTimer = setInterval(() => {
        setPhase(prev => {
          if (prev === 'indexing') return 'researching';
          if (prev === 'researching') return 'writing';
          if (prev === 'writing') return 'classifying';
          if (prev === 'classifying') return 'saving';
          return prev;
        });
      }, 8000);
      
      try {
        const result = await generateSinglePlaybook({
          sourceId: selectedSource.sourceId,
          topic: topic.trim(),
          category: categoryValue,
        });
        
        clearInterval(phaseTimer);
        return result;
      } catch (error) {
        clearInterval(phaseTimer);
        throw error;
      }
    },
    onSuccess: (data) => {
      setResult(data);
      setStep('success');
      toast({
        title: 'Playbook Generated',
        description: `Successfully created playbook at ${data.playbookPath}`,
      });
    },
    onError: (error) => {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to generate playbook');
      setStep('error');
      toast({
        title: 'Generation Failed',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    },
  });
  
  // Handle generate button click
  const handleGenerate = () => {
    if (!selectedSource || !topic.trim()) {
      toast({
        title: 'Missing Information',
        description: 'Please select or create a source and enter a topic',
        variant: 'destructive',
      });
      return;
    }
    
    setStep('generating');
    setPhase('indexing');
    generateMutation.mutate();
  };
  
  // Reset to configure state
  const handleReset = () => {
    setStep('configure');
    setSelectedSource(null);
    setTopic('');
    setResult(null);
    setErrorMessage('');
  };
  
  // Get phase description
  const getPhaseDescription = (currentPhase: GenerationPhase): string => {
    switch (currentPhase) {
      case 'indexing':
        return 'Indexing source documents...';
      case 'researching':
        return 'Researching topic across documents...';
      case 'writing':
        return 'Synthesizing playbook content...';
      case 'classifying':
        return 'Classifying playbook...';
      case 'saving':
        return 'Saving playbook...';
      default:
        return 'Processing...';
    }
  };
  
  // Render configuration step
  const renderConfigureStep = () => (
    <div className="space-y-6">
      {/* Step 1: Source Selection */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-semibold">
            1
          </div>
          <Label className="text-base font-medium">Choose Document Source</Label>
        </div>
        
        <div className="pl-8">
          <DynamicSourceSelector
            sources={sources || []}
            isLoadingSources={isLoadingSources}
            onSourceSelected={handleSourceSelected}
            disabled={generateMutation.isPending}
          />
        </div>
      </div>
      
      {/* Step 2: Topic Input */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-semibold">
            2
          </div>
          <Label className="text-base font-medium">Define Playbook Topic</Label>
        </div>
        
        <Input
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="Enter the playbook topic..."
          className="w-full"
        />
        
        <p className="text-sm text-muted-foreground pl-8">
          Examples: "Security standards for PII", "ERP integration guide", "API authentication patterns"
        </p>
      </div>
      
      {/* Step 3: Category Selection */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-semibold">
            3
          </div>
          <Label className="text-base font-medium">Category (Optional)</Label>
        </div>
        
        <div className="pl-8">
          <Select value={category} onValueChange={(value: CategoryOption) => setCategory(value)}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Auto-detect" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto-detect</SelectItem>
              <SelectItem value="product">Product</SelectItem>
              <SelectItem value="organizational">Organizational</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Generate Button */}
      <div className="pt-4">
        <Button
          onClick={handleGenerate}
          disabled={!selectedSource || !topic.trim() || generateMutation.isPending}
          className="w-full sm:w-auto"
          size="lg"
        >
          <Sparkles className="h-4 w-4 mr-2" />
          Generate Playbook
        </Button>
      </div>
    </div>
  );
  
  // Render generating step
  const renderGeneratingStep = () => (
    <div className="flex flex-col items-center justify-center py-12 space-y-6">
      <div className="relative">
        <Loader2 className="h-12 w-12 text-primary animate-spin" />
      </div>
      
      <div className="text-center space-y-2">
        <h3 className="text-lg font-semibold">Generating Playbook</h3>
        <p className="text-muted-foreground">{getPhaseDescription(phase)}</p>
      </div>
      
      {/* Progress indicator */}
      <div className="flex items-center gap-2">
        {(['indexing', 'researching', 'writing', 'classifying', 'saving'] as GenerationPhase[]).map((p, idx) => (
          <div
            key={p}
            className={`w-3 h-3 rounded-full transition-colors ${
              p === phase 
                ? 'bg-primary animate-pulse' 
                : (['indexing', 'researching', 'writing', 'classifying', 'saving'].indexOf(p) < ['indexing', 'researching', 'writing', 'classifying', 'saving'].indexOf(phase))
                  ? 'bg-primary'
                  : 'bg-muted'
            }`}
          />
        ))}
      </div>
      
      <div className="text-sm text-muted-foreground space-y-1">
        <div>Topic: "{topic}"</div>
        {selectedSource && (
          <div>Source: {selectedSource.sourceName} ({selectedSource.sourceType})</div>
        )}
      </div>
    </div>
  );
  
  // Render success step
  const renderSuccessStep = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3 text-green-600">
        <CheckCircle className="h-8 w-8" />
        <h3 className="text-xl font-semibold">Playbook Generated Successfully</h3>
      </div>
      
      {result && (
        <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Path:</span>
              <p className="font-medium">{result.playbookPath}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Domain:</span>
              <p className="font-medium">{result.classification.domain}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Scope:</span>
              <p className="font-medium capitalize">{result.classification.scope}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Confidence:</span>
              <p className="font-medium">{Math.round(result.classification.confidence * 100)}%</p>
            </div>
          </div>
          
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium mb-2">Generation Stats</h4>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Documents Indexed:</span>
                <p className="font-medium">{result.indexingStats.documentsIndexed}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Chunks Created:</span>
                <p className="font-medium">{result.indexingStats.chunksCreated}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Queries Made:</span>
                <p className="font-medium">{result.generationStats.queriesMade}</p>
              </div>
            </div>
            <div className="mt-2 text-sm text-muted-foreground">
              Total time: {(result.totalTimeMs / 1000).toFixed(1)}s
            </div>
          </div>
        </div>
      )}
      
      <div className="flex gap-3">
        <Button onClick={handleReset} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Generate Another
        </Button>
        <Button variant="outline" asChild>
          <a href={`/playbooks?path=${encodeURIComponent(result?.playbookPath || '')}`}>
            <ExternalLink className="h-4 w-4 mr-2" />
            View Playbook
          </a>
        </Button>
      </div>
    </div>
  );
  
  // Render error step
  const renderErrorStep = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3 text-destructive">
        <AlertCircle className="h-8 w-8" />
        <h3 className="text-xl font-semibold">Generation Failed</h3>
      </div>
      
      <div className="flex items-center gap-2 p-3 text-sm border border-destructive/50 rounded-md bg-destructive/10 text-destructive">
        <AlertCircle className="h-4 w-4 flex-shrink-0" />
        <span>{errorMessage}</span>
      </div>
      
      <div className="flex gap-3">
        <Button onClick={handleReset} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Try Again
        </Button>
      </div>
    </div>
  );
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <BookOpen className="h-5 w-5 text-primary" />
        <div>
          <h2 className="text-lg font-semibold">Generate Playbook</h2>
          <p className="text-sm text-muted-foreground">
            Create a playbook from indexed knowledge source documents
          </p>
        </div>
      </div>
      
      {/* Main content area */}
      <div className="border rounded-lg p-6">
        {step === 'configure' && renderConfigureStep()}
        {step === 'generating' && renderGeneratingStep()}
        {step === 'success' && renderSuccessStep()}
        {step === 'error' && renderErrorStep()}
      </div>
    </div>
  );
}
